import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FindingsTable, { type FindingSortKey } from '@components/findings/FindingsTable';
import BulkTriageBar from '@components/findings/BulkTriageBar';
import { useBulkTriage } from '@components/findings/useBulkTriage';
import {
  findingsService,
  ALL_FINDING_STATUSES,
  type OrgFindingsPage,
} from '@services/findingsService';
import type { Finding } from '@services/auditService';
import { sortAndFilterFindingRows } from '@utils/scanRunFindings';
import { toDisplayError } from '@utils/apiError';
import type { GroupSeverity } from '@utils/scanRunGroups';

const ROWS_PER_PAGE = 50;

export interface RuleInstancesProps {
  scanRunId: string;
  ruleId: string;
  /** The page's severity filter, applied to the instances too. */
  severity?: GroupSeverity;
  /**
   * Whether this render reads `/findings` (a tracked place and an active
   * organization); otherwise `ephemeralRows` are the instances. See
   * ScanRunFindings for why there are two sources.
   */
  usesEndpoint: boolean;
  organizationId?: string;
  /** REV-949: whether the caller's role may change a finding's status. */
  canTriage: boolean;
  orgSlug?: string;
  /** Every Mongo-backed row of the run, for a render that cannot use the endpoint. */
  ephemeralRows: Finding[];
  /** The engine's per-finding messages, keyed by row id (Mongo rows only). */
  messages: ReadonlyMap<string, string>;
  /** id of the heading that names this rule's instances. */
  labelledBy: string;
  regionLabel: string;
}

/**
 * REV-715: the instances of ONE rule in a scan run — the third level of the
 * grouped results.
 *
 * Mounted only when its rule is opened, so a run is not fetched rule by rule
 * before anyone asks. Pages on the server for a tracked place (the grouping
 * comes from the run's aggregates, never from a page of these rows), in memory
 * for an untracked URL, whose rows are all already in the page's state.
 *
 * Everything the flat table offered stays here, per rule: the finding-detail
 * link, severity sort, pagination, and bulk triage. Selection is per rule, the
 * way AFixt-Investigator's "Log all" is per test, so "select all" can only ever
 * mean the instances in front of the reader.
 */
export default function RuleInstances({
  scanRunId,
  ruleId,
  severity,
  usesEndpoint,
  organizationId,
  canTriage,
  orgSlug,
  ephemeralRows,
  messages,
  labelledBy,
  regionLabel,
}: RuleInstancesProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [fetched, setFetched] = useState<OrgFindingsPage | null>(null);
  const [loading, setLoading] = useState(usesEndpoint);
  const [error, setError] = useState<string | null>(null);
  // Within one rule only severity is worth sorting on; the Rule header would
  // reorder rows that all carry the same rule, so it is not a sort control.
  //
  // No page reset on a new severity here: the parent keys this component by
  // rule AND severity, so a filter change remounts it on page one with a
  // single fetch. Resetting in an effect ran after the render, fetching the
  // new severity at the old offset first (found in review).
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const onSort = (_by: FindingSortKey) => {
    setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    setPage(0);
  };

  const shownEphemeral = useMemo(
    () =>
      sortAndFilterFindingRows(ephemeralRows, {
        by: 'severity',
        direction,
        severity,
        ruleId,
      }),
    [ephemeralRows, direction, severity, ruleId]
  );

  const load = useCallback(async () => {
    if (!usesEndpoint || !organizationId) return;
    setLoading(true);
    setError(null);
    try {
      setFetched(
        await findingsService.listForOrg(organizationId, {
          scan_run_id: scanRunId,
          rule_id: ruleId,
          status: ALL_FINDING_STATUSES,
          sort: 'severity',
          direction,
          ...(severity ? { severity } : {}),
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        })
      );
    } catch (err) {
      setError(
        toDisplayError(err, t('scanFindings.errors.fetchFailed', 'Failed to load these findings'))
      );
    } finally {
      setLoading(false);
    }
  }, [usesEndpoint, organizationId, scanRunId, ruleId, direction, severity, rowsPerPage, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: Finding[] = usesEndpoint
    ? (fetched?.items ?? [])
    : shownEphemeral.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const total = usesEndpoint ? (fetched?.pagination.total ?? 0) : shownEphemeral.length;

  const hrefFor = (f: Finding): string | undefined =>
    usesEndpoint && orgSlug && f.raw_id ? `/orgs/${orgSlug}/findings/${f.raw_id}` : undefined;

  const bulk = useBulkTriage({
    // Only a render that reads /findings has FindingInstances to act on; no
    // organization is how "selection is impossible here" is said, and the
    // checkbox column is then absent rather than present and disabled.
    organizationId: usesEndpoint ? organizationId : undefined,
    // REV-949: nor does a viewer, who can read findings but not change them.
    canTriage,
    rows,
    onChanged: load,
    onError: setError,
  });

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <BulkTriageBar bulk={bulk} />
      <FindingsTable
        rows={rows}
        selection={bulk.selection}
        loading={loading}
        labelledBy={labelledBy}
        regionLabel={regionLabel}
        // The rule cell stays even though every row shares the rule: it is the
        // link to finding detail and carries the engine's per-finding message.
        columns={['rule', 'severity', 'target', 'wcag']}
        hrefFor={hrefFor}
        sort={{ by: 'severity', direction, onSort, sortable: ['severity'] }}
        descriptionFor={usesEndpoint ? undefined : (f) => messages.get(f.id)}
        empty={
          error ? null : t('scanFindings.ruleEmpty', 'No findings of this rule match the filters.')
        }
        footer={
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        }
      />
    </>
  );
}
